import React, { useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import './CancelSubscriptionModal.scss';
import axiosInstance from '../../api/axiosRest';
import { SUBSCRIPTION } from '../../api/urls';
import { toast } from 'react-toastify';

const CancelSubscriptionModal = ({ 
  show, 
  onHide, 
  subscriptionData, 
  onSubscriptionCanceled 
}) => {

  const [isLoading, setIsLoading] = useState(false);
  const [confirmationStep, setConfirmationStep] = useState(1);
  const [cancelationType, setCancelationType] = useState('end_of_period');
  const [confirmationText, setConfirmationText] = useState('');

  const handleCancel = async () => {
    setIsLoading(true);
    
    try {
      const payload = {
        immediate: cancelationType === 'immediate'
      };
      
      const response = await axiosInstance.post(SUBSCRIPTION.CANCEL, payload);
      
      if (response.status === 200) {
        toast.success(response.data.message);
        onSubscriptionCanceled({
          status: 'canceled',
          canceled_at_period_end: response.data.canceled_at_period_end,
          access_until: response.data.access_until
        });
        onHide();
        setConfirmationStep(1);
      }
    } catch (error) {
      console.error('Error canceling subscription:', error);
      const errorMessage = error.response?.data?.error || 'Failed to cancel subscription. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onHide();
      setConfirmationStep(1);
      setCancelationType('end_of_period');
      setConfirmationText('');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return 'N/A';
    }
  };

  return (
    <Modal 
      show={show} 
      onHide={handleClose} 
      centered
      backdrop={isLoading ? 'static' : true}
      keyboard={!isLoading}
      className="cancel-subscription-modal"
    >
      <Modal.Header 
        closeButton={!isLoading}
        className="cancel-modal-header"
      >
        <Modal.Title className="d-flex align-items-center">
          <Icon 
            icon="fa-solid:exclamation-triangle" 
            className="me-2 text-warning" 
            width={24} 
            height={24} 
          />
          Cancel Subscription
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="cancel-modal-body">
        {confirmationStep === 1 && (
          <div className="confirmation-step-1">
            <div className="subscription-info mb-4">
              <h5 className="text-white mb-3">Current Subscription Details</h5>
              <div className="info-card">
                <div className="info-row">
                  <span className="label">Plan:</span>
                  <span className="value">{subscriptionData?.plan || 'N/A'}</span>
                </div>
                <div className="info-row">
                  <span className="label">Access Until:</span>
                  <span className="value">
                    {subscriptionData?.current_period_end ? 
                      formatDate(subscriptionData.current_period_end) : 
                      'N/A'
                    }
                  </span>
                </div>
                <div className="info-row">
                  <span className="label">Status:</span>
                  <span className="value status-badge">
                    {subscriptionData?.subscription_status ? 
                      subscriptionData.subscription_status.charAt(0).toUpperCase() + subscriptionData.subscription_status.slice(1) 
                      : 'N/A'
                    }
                  </span>
                </div>
              </div>
            </div>

            <div className="cancellation-options mb-4">
              <h6 className="text-white mb-3">Choose Cancellation Type</h6>
              
              <div className="option-card">
                <div className="form-check">
                  <input 
                    className="form-check-input" 
                    type="radio" 
                    name="cancelationType" 
                    id="endOfPeriod"
                    value="end_of_period"
                    checked={cancelationType === 'end_of_period'}
                    onChange={(e) => setCancelationType(e.target.value)}
                  />
                  <label className="form-check-label" htmlFor="endOfPeriod">
                    <div className="option-content">
                      <div className="option-title">
                        <Icon icon="fa-solid:clock" className="me-2" />
                        Cancel at Period End (Recommended)
                      </div>
                      <div className="option-description">
                        Keep access until {formatDate(subscriptionData?.current_period_end)}. 
                        No refund, but you get full value for what you've paid.
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              <div className="option-card">
                <div className="form-check">
                  <input 
                    className="form-check-input" 
                    type="radio" 
                    name="cancelationType" 
                    id="immediate"
                    value="immediate"
                    checked={cancelationType === 'immediate'}
                    onChange={(e) => setCancelationType(e.target.value)}
                  />
                  <label className="form-check-label" htmlFor="immediate">
                    <div className="option-content">
                      <div className="option-title">
                        <Icon icon="fa-solid:times-circle" className="me-2" />
                        Cancel Immediately
                      </div>
                      <div className="option-description">
                        Lose access right away. No refund will be processed.
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            </div>

            <div className="warning-message">
              <Icon icon="fa-solid:info-circle" className="me-2" />
              <span>
                Are you sure you want to cancel your subscription? 
                This action cannot be undone easily.
              </span>
            </div>
          </div>
        )}

        {confirmationStep === 2 && (
          <div className="confirmation-step-2">
            <div className="final-confirmation">
              <div className="confirmation-icon mb-3">
                <Icon 
                  icon="fa-solid:exclamation-triangle" 
                  width={48} 
                  height={48} 
                  className="text-danger" 
                />
              </div>
              <h5 className="text-white mb-3">Final Confirmation</h5>
              <p className="confirmation-text">
                {cancelationType === 'immediate' 
                  ? 'You are about to cancel your subscription immediately. You will lose access right away and no refund will be processed.'
                  : `You are about to cancel your subscription at the end of your current billing period (${formatDate(subscriptionData?.current_period_end)}). You will keep access until then.`
                }
              </p>
              <div className="type-to-confirm mt-3">
                <p className="mb-2">Type <strong>CANCEL</strong> to confirm:</p>
                <input 
                  type="text" 
                  className="form-control confirm-input"
                  placeholder="Type CANCEL here"
                  id="confirmationText"
                  value={confirmationText}
                  onChange={(e) => setConfirmationText(e.target.value.toUpperCase())}
                />
              </div>
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="cancel-modal-footer">
        {confirmationStep === 1 && (
          <>
            <Button 
              variant="secondary" 
              onClick={handleClose}
              disabled={isLoading}
              className="cancel-btn-secondary"
            >
              <Icon icon="fa-solid:times" className="me-2" />
              Keep Subscription
            </Button>
            <Button 
              variant="warning" 
              onClick={() => setConfirmationStep(2)}
              disabled={isLoading}
              className="cancel-btn-warning"
            >
              <Icon icon="fa-solid:arrow-right" className="me-2" />
              Continue Cancellation
            </Button>
          </>
        )}

        {confirmationStep === 2 && (
          <>
            <Button 
              variant="secondary" 
              onClick={() => setConfirmationStep(1)}
              disabled={isLoading}
              className="cancel-btn-secondary"
            >
              <Icon icon="fa-solid:arrow-left" className="me-2" />
              Back
            </Button>
            <Button 
              variant="danger" 
              onClick={handleCancel}
              disabled={isLoading || confirmationText !== 'CANCEL'}
              className="cancel-btn-danger"
            >
              {isLoading ? (
                <>
                  <Icon icon="fa-solid:spinner" className="me-2 fa-spin" />
                  Canceling...
                </>
              ) : (
                <>
                  <Icon icon="fa-solid:trash" className="me-2" />
                  {cancelationType === 'immediate' ? 'Cancel Immediately' : 'Cancel Subscription'}
                </>
              )}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default CancelSubscriptionModal;
